/* eslint-disable react/prop-types */
import NumberSlideBar from '@frame/components/property-editor/editor-components/number-slidebar';
import i18n from '@i18next/i18n';
import ColorPickerContainer from '@src/js/common/ark-extensions/dc-components/color-picker/color-picker-container';
import { ColorPickerType } from '@src/js/common/ark-extensions/dc-components/color-picker/type';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import type { TaskCardComponent } from '@tencent/ai-sidebar-sdk';
import ua from '@tencent/docs-user-agent';
import Dropdown from '@tencent/dui/lib/components/Dropdown';
import classNames from 'classnames';
import React, { useState } from 'react';
import { BackgroundFillTask, getGradFillStyle, GradPreset } from '../';
import { GradColorResult, SolidColorResult } from '../interface';
import styles from './palette-card.less';

interface Props extends SolidColorResult {}

export const createSolidPalette = (backgroundFillTask: BackgroundFillTask) => {
    const SolidPalette: TaskCardComponent<Props> = ({ props, onResult, disable }) => {
        const colorOptions = backgroundFillTask.getColorOptions() ?? [];
        const [enableCustom, setEnableCustom] = useState<boolean>(props?.enableCustom ?? false);
        const [themeKey, setThemeKey] = useState<number>(props?.themeKey ?? colorOptions[0].themeKey);
        const [customColor, setCustomColor] = useState<TDColor>(new TDColor({ themeKey }));
        const [transparency, setTransparency] = useState<number>(props?.transparency ?? 0);

        return (
            <div
                className={classNames({
                    'color-options-card': true,
                    'color-options-card--pc': !ua.isMobile,
                    'color-options-card--mobile': ua.isMobile,
                })}
                style={{
                    pointerEvents: disable ? 'none' : 'unset',
                }}
            >
                <p className="color-options-card-description">{i18n.t('请选择你喜欢的配色')}</p>
                <ul className={styles.colorList}>
                    <li
                        className={classNames({
                            [styles.palette]: true,
                            [styles.color]: true,
                            [styles.selected]: enableCustom,
                        })}
                        onClick={() => setEnableCustom(true)}
                    >
                        <Dropdown
                            alignment="auto-outer"
                            dropContent={
                                <ColorPickerContainer
                                    colorPickerType={ColorPickerType.StandardPC}
                                    selectedTdColor={customColor}
                                    onColorChange={(color) => {
                                        setCustomColor(color);
                                    }}
                                />
                            }
                        >
                            <div style={{ width: 30, height: 30 }} />
                        </Dropdown>
                    </li>

                    {colorOptions.map(colorOption => (
                        <li
                            key={colorOption.themeKey}
                            style={{ backgroundColor: colorOption.color }}
                            className={classNames({
                                [styles.color]: true,
                                [styles.selected]: !enableCustom && colorOption.themeKey === themeKey,
                            })}
                            onClick={() => {
                                setEnableCustom(false);
                                setThemeKey(colorOption.themeKey);
                            }}
                        />
                    ))}
                </ul>

                <Slider value={transparency} onChange={setTransparency} />

                {!disable && (
                    <button
                        className="ai-assistant-sdk-button ai-assistant-sdk-button--primary"
                        onClick={() => {
                            const result: SolidColorResult = {
                                enableCustom,
                                themeKey,
                                customColor,
                                transparency,
                            };
                            onResult(result, { ...props, ...result }, i18n.t('选择完成'));
                        }}
                    >
                        {i18n.t('选择完成')}
                    </button>
                )}
            </div>
        );
    };
    return SolidPalette;
};

export const GradPalette: TaskCardComponent<Props> = ({ props, onResult, disable }) => {
    const [gradColor, setGradColor] = useState<[string, string]>(GradPreset[0]);
    const [transparency, setTransparency] = useState<number>(0);

    return (
        <div
            className={classNames({
                'color-options-card': true,
                'color-options-card--pc': !ua.isMobile,
                'color-options-card--mobile': ua.isMobile,
            })}
            style={{
                pointerEvents: disable ? 'none' : 'unset',
            }}
        >
            <p className="color-options-card-description">{i18n.t('请选择你喜欢的配色')}</p>
            <ul className={styles.colorList}>
                {GradPreset.map((color, index) => (
                    <li
                        key={index}
                        style={{ background: getGradFillStyle(...color), backgroundClip: 'content-box' }}
                        className={classNames({
                            [styles.color]: true,
                            [styles.selected]: color === gradColor,
                        })}
                        onClick={() => {
                            setGradColor(color);
                        }}
                    />
                ))}
            </ul>

            <Slider value={transparency} onChange={setTransparency} />

            {!disable && (
                <button
                    className="ai-assistant-sdk-button ai-assistant-sdk-button--primary"
                    onClick={() => {
                        const result: GradColorResult = {
                            gradColor,
                            transparency,
                        };
                        onResult(result, { ...props, ...result }, i18n.t('选择完成'));
                    }}
                >
                    {i18n.t('选择完成')}
                </button>
            )}
        </div>
    );
};

interface SliderProps {
    value: number;
    onChange: (value: number) => void;
}
// eslint-disable-next-line arrow-body-style
export const Slider = (props: SliderProps) => {
    return (
        <div className={styles.sliderWrapper}>
            <NumberSlideBar
                title={i18n.t('透明度')}
                value={props.value}
                toFixed={2}
                slideBarStep={1}
                numberInputStep={1}
                lowerLimit={0}
                upperLimit={100}
                allowDecimal
                postFix="%"
                sliderStyle={{ width: 144 }}
                sliderContainerStyle={{ marginTop: 0 }}
                type="opacity"
                onBlur={() => {}}
                onFocus={() => {}}
                onValueChange={(_, value) => props.onChange(value)}
                onValueEnter={() => {}}
                onValueLeave={() => {}}
                canShowEmptyString={true}
                isIntegerOfStep={true}
            />
        </div>
    );
};
